import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  fallback: ReactNode;
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
}

// Generic, reusable error boundary (F-P3). React only supports error
// boundaries as class components — there is no hooks equivalent.
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Diagnostic exception to the no-console rule — this is the only place
    // a render-phase crash gets surfaced at all.
    console.error("ErrorBoundary caught an error:", error, info.componentStack);
    this.props.onError?.(error, info);
  }

  override render(): ReactNode {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

// Full-page fallback for the top-level boundary wrapping the router/app.
// A plain reload (not a boundary "reset") is the only recovery that can fix
// the most common cause — a stale index.html referencing a dead hashed chunk.
export function AppCrashFallback(): JSX.Element {
  return (
    <div className="grid place-items-center min-h-screen p-8" role="alert">
      <div className="max-w-md w-full text-center">
        <h1 className="font-display text-2xl font-extrabold mb-2">Something went wrong</h1>
        <p className="text-muted-foreground mb-6">
          We hit a snag loading the app. Reloading the page usually fixes it.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="bg-primary text-primary-foreground rounded-control px-4 py-2 text-sm font-semibold"
        >
          Reload page
        </button>
      </div>
    </div>
  );
}
